import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Slider } from '@/components/ui/slider';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Eye, Sparkles, Calendar, CheckCircle2, Zap, Play, Square, Lightbulb } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import { format, startOfWeek, endOfWeek, eachDayOfInterval } from 'date-fns';

const exercises = [
  { 
    id: 'sitting-in-power', 
    name: 'Sitting in the Power',
    instructions: 'Sit in a quiet space with feet flat on the floor. Ground yourself by visualising roots growing from your feet deep into the earth. Draw earth energy up through your body. Open your crown chakra and invite spiritual energy to flow down. Allow both energies to blend and expand your energy field. Sit in this elevated state for 20-30 minutes, simply being present and observing any sensations, images, feelings, or knowings that arise. This is not meditation - you are actively sitting in your spiritual power.',
    benefits: 'THE foundational practice taught in "The Psychic Psychologist®." This builds your spiritual power, strengthens your connection to spirit, increases your sensitivity to subtle energies, and develops the stamina needed for readings. Regular daily practice is essential - it transforms your abilities and raises your baseline vibration. This is where all psychic and mediumship development begins.',
    prompts: [
      'How strong was your connection today? Rate 1-10',
      'What sensations did you notice in your body or energy field?',
      'Did any images, messages, or impressions come through?',
      'How long were you able to maintain the elevated state?'
    ],
    recommended: true
  },
  { 
    id: 'visualisation', 
    name: 'Visualisation Practice',
    instructions: 'Close your eyes and picture a simple object (apple, candle, crystal) in your mind\'s eye. Hold the image for 5 minutes, noticing colors, textures, and details. When the image fades, bring it back gently.',
    benefits: 'Strengthens your ability to create and hold mental images - the foundation of clairvoyant vision. Regular practice builds the "screen" upon which psychic visions appear.',
    prompts: [
      'What object did you visualise today?',
      'How clear was the image? Rate 1-10',
      'Did any unexpected details or colors appear?',
      'How long could you hold the image before it faded?'
    ]
  },
  { 
    id: 'aura-viewing', 
    name: 'Aura Viewing',
    instructions: 'Look at your hand against a white/neutral background. Soften your gaze (peripheral vision) and look *through* your hand rather than at it. Notice subtle colors, light, or haze around the edges. Start with 3-5 minutes.',
    benefits: 'Opens your ability to perceive energy fields and subtle light. This is the gateway to seeing auras, spirits, and energetic phenomena around people and objects.',
    prompts: [
      'What colors or sensations did you perceive?',
      'Did you see any movement in the energy field?',
      'How did it feel to shift to soft-focus vision?',
      'Any insights about the energy you observed?'
    ]
  },
  { 
    id: 'symbol-meditation', 
    name: 'Symbol Meditation',
    instructions: 'Choose a sacred symbol (eye of Horus, third eye chakra symbol, infinity sign). Gaze at it for 2 minutes, then close your eyes and recreate it mentally. Notice any messages, feelings, or visions that arise from the symbol.',
    benefits: 'Sacred symbols are "keys" that unlock psychic centers. They carry ancient wisdom and activate the third eye through their sacred geometry and intention.',
    prompts: [
      'Which symbol did you work with today?',
      'What came through during the meditation?',
      'Did the symbol transform or shift?',
      'What feelings or knowings arose?'
    ]
  },
  { 
    id: 'candle-gazing', 
    name: 'Candle Gazing (Trataka)',
    instructions: 'Light a candle at eye level, 2-3 feet away. Stare at the flame without blinking for 1-2 minutes. Close your eyes and observe the afterimage. Watch what shapes, colors, or visions emerge from the flame impression.',
    benefits: 'Ancient technique to purify and strengthen the third eye. The flame afterimage creates a "canvas" for psychic visions to appear. Develops concentration and psychic sight simultaneously.',
    prompts: [
      'What did you see in the afterimage?',
      'Did any faces, symbols, or scenes appear?',
      'How did your third eye area feel during practice?',
      'Any messages received through the flame?'
    ]
  },
  { 
    id: 'remote-viewing', 
    name: 'Remote Viewing Practice',
    instructions: 'Choose a location you\'ve never been to (friend\'s house, random address, distant landmark). Close your eyes, breathe deeply, and intend to "travel" there psychically. Notice any impressions: colors, textures, emotions, shapes. Don\'t judge, just receive.',
    benefits: 'Trains your third eye to "see" beyond physical location and time. Develops trust in psychic impressions and expands the range of your clairvoyant abilities.',
    prompts: [
      'What location did you remote view?',
      'What impressions did you receive? (colors, shapes, feelings)',
      'Were you able to validate any details?',
      'What surprised you about the experience?'
    ]
  },
  { 
    id: 'indigo-breathing', 
    name: 'Indigo Light Breathing',
    instructions: 'Sit comfortably, close your eyes. Visualise indigo/purple light flowing in through your third eye with each inhale. Feel it swirling, energizing the area between your eyebrows. Exhale any blockages or tension. Continue for 5-10 minutes.',
    benefits: 'Directly activates and cleanses the third eye chakra. Indigo is the frequency of intuition and inner vision. This practice "charges" your psychic center with healing light.',
    prompts: [
      'How did your third eye area feel during and after?',
      'Did you experience any tingling, pressure, or warmth?',
      'What visions or colors appeared?',
      'How has your intuition felt since the practice?'
    ]
  }
];

interface Practice {
  id: string;
  user_id: string;
  date: string;
  exercise_id: string;
  exercise_name: string;
  custom_practice_name?: string | null;
  journal_responses: { prompt: string; response: string }[];
  created_at: string;
}

interface PowerSession {
  id: string;
  user_id: string;
  session_date: string;
  duration_minutes: number;
  energy_quality: string;
  sensations_noted: string;
  insights_received: string;
  power_rating: number;
  created_at: string;
}

export default function ThirdEyeDevelopment() {
  // Check URL for tab parameter to support direct navigation
  const urlParams = new URLSearchParams(window.location.search);
  const defaultTab = urlParams.get('tab') || 'practices';
  
  const [selectedExercise, setSelectedExercise] = useState<string | null>(null);
  const [customPracticeName, setCustomPracticeName] = useState('');
  const [showCustomDialog, setShowCustomDialog] = useState(false);
  const [journalResponses, setJournalResponses] = useState<{ prompt: string; response: string }[]>([]);
  const [practices, setPractices] = useState<Practice[]>([]);
  const [weekDates, setWeekDates] = useState<Date[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Sitting in Power states
  const [powerSessions, setPowerSessions] = useState<PowerSession[]>([]);
  const [isSessionActive, setIsSessionActive] = useState(false);
  const [sessionStart, setSessionStart] = useState<Date | null>(null);
  const [timer, setTimer] = useState(0);
  const [powerFormData, setPowerFormData] = useState({
    energy_quality: '',
    sensations_noted: '',
    insights_received: '',
    power_rating: 5
  });
  
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    loadPractices();
    loadPowerSessions();
    calculateWeekDates();
  }, []);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isSessionActive && sessionStart) {
      interval = setInterval(() => {
        const elapsed = Math.floor((Date.now() - sessionStart.getTime()) / 1000);
        setTimer(elapsed);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isSessionActive, sessionStart]);

  const calculateWeekDates = () => {
    const today = new Date();
    const start = startOfWeek(today, { weekStartsOn: 1 }); // Monday
    const end = endOfWeek(today, { weekStartsOn: 1 }); // Sunday
    const dates = eachDayOfInterval({ start, end });
    setWeekDates(dates);
  };

  const loadPractices = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { data, error } = await supabase
      .from('third_eye_practices')
      .select('*')
      .eq('user_id', user.id)
      .order('date', { ascending: false });

    if (error) {
      console.error('Error loading practices:', error);
    } else {
      // Type cast the journal_responses from Json to the correct type
      const typedData = (data || []).map(practice => ({
        ...practice,
        journal_responses: practice.journal_responses as unknown as { prompt: string; response: string }[]
      }));
      setPractices(typedData);
    }
    setLoading(false);
  };

  const loadPowerSessions = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { data, error } = await supabase
      .from('sitting_in_power_sessions')
      .select('*')
      .eq('user_id', user.id)
      .order('session_date', { ascending: false })
      .limit(10);

    if (error) {
      console.error('Error loading power sessions:', error);
    } else {
      setPowerSessions(data || []);
    }
  };

  const handleExerciseSelect = (exerciseId: string) => {
    const exercise = exercises.find(e => e.id === exerciseId);
    if (!exercise) return;

    setSelectedExercise(exerciseId);
    setJournalResponses(exercise.prompts.map(prompt => ({ prompt, response: '' })));
  };

  const handleCustomExercise = () => {
    setShowCustomDialog(true);
  };

  const startCustomPractice = () => {
    if (!customPracticeName.trim()) {
      toast({ title: 'Please enter a practice name', variant: 'destructive' });
      return;
    }
    setSelectedExercise('custom');
    setJournalResponses([
      { prompt: 'Describe your practice:', response: '' },
      { prompt: 'Why did you choose this practice?', response: '' },
      { prompt: 'What did you experience?', response: '' },
      { prompt: 'How did it support your third eye development?', response: '' }
    ]);
    setShowCustomDialog(false);
  };

  const handleResponseChange = (index: number, value: string) => {
    const updated = [...journalResponses];
    updated[index].response = value;
    setJournalResponses(updated);
  };

  const handleSavePractice = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    if (!selectedExercise) {
      toast({ title: 'Please select a practice', variant: 'destructive' });
      return;
    }

    const hasResponses = journalResponses.some(r => r.response.trim());
    if (!hasResponses) {
      toast({ title: 'Please answer at least one journal prompt', variant: 'destructive' });
      return;
    }

    const exercise = exercises.find(e => e.id === selectedExercise);
    const practiceName = selectedExercise === 'custom' ? customPracticeName : exercise?.name;

    const { error } = await supabase
      .from('third_eye_practices')
      .insert({
        user_id: user.id,
        date: new Date().toISOString().split('T')[0],
        exercise_id: selectedExercise,
        exercise_name: practiceName,
        custom_practice_name: selectedExercise === 'custom' ? customPracticeName : null,
        journal_responses: journalResponses.filter(r => r.response.trim())
      });

    if (error) {
      toast({ title: 'Error saving practice', variant: 'destructive' });
    } else {
      toast({ title: '✨ Practice saved!', description: 'Your third eye development is tracked.' });
      setSelectedExercise(null);
      setJournalResponses([]);
      setCustomPracticeName('');
      loadPractices();
    }
  };

  const getPracticesForDate = (date: Date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return practices.filter(p => p.date === dateStr);
  };

  const getWeekStats = () => {
    if (weekDates.length === 0) {
      return { total: 0, days: 0, exercises: {} };
    }
    
    const weekStart = format(weekDates[0], 'yyyy-MM-dd');
    const weekEnd = format(weekDates[weekDates.length - 1], 'yyyy-MM-dd');
    const weekPractices = practices.filter(p => p.date >= weekStart && p.date <= weekEnd);
    
    return {
      total: weekPractices.length,
      days: new Set(weekPractices.map(p => p.date)).size,
      exercises: weekPractices.reduce((acc, p) => {
        acc[p.exercise_name] = (acc[p.exercise_name] || 0) + 1;
        return acc;
      }, {} as Record<string, number>)
    };
  };

  // Sitting in Power functions
  const startSession = () => {
    setIsSessionActive(true);
    setSessionStart(new Date());
    setTimer(0);
  };

  const endSession = () => {
    setIsSessionActive(false);
  };

  const savePowerSession = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const durationMinutes = Math.floor(timer / 60);

    const { error } = await supabase
      .from('sitting_in_power_sessions')
      .insert({
        user_id: user.id,
        session_date: new Date().toISOString().split('T')[0],
        duration_minutes: durationMinutes,
        energy_quality: powerFormData.energy_quality,
        sensations_noted: powerFormData.sensations_noted,
        insights_received: powerFormData.insights_received,
        power_rating: powerFormData.power_rating
      });

    if (error) {
      toast({ title: 'Error saving session', variant: 'destructive' });
    } else {
      toast({ title: '✨ Session saved successfully!' });
      setPowerFormData({
        energy_quality: '',
        sensations_noted: '',
        insights_received: '',
        power_rating: 5
      });
      setTimer(0);
      loadPowerSessions();
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const selectedExerciseData = exercises.find(e => e.id === selectedExercise);
  const weekStats = getWeekStats();

  if (loading) {
    return <div className="text-center py-8 font-light">Loading your practices...</div>;
  }

  return (
    <div className="h-full w-full max-w-full max-h-[calc(100vh-12rem)] overflow-hidden">
      <Tabs defaultValue={defaultTab} className="w-full max-w-full h-full flex flex-col overflow-hidden">
        <TabsList className="grid w-full grid-cols-2 mb-4 flex-shrink-0">
          <TabsTrigger value="practices" className="font-light text-sm">
            <Eye className="h-4 w-4 mr-2" />
            <span className="hidden sm:inline">Clairvoyance Practices</span>
            <span className="sm:hidden">Practices</span>
          </TabsTrigger>
          <TabsTrigger value="power" className="font-light text-sm">
            <Zap className="h-4 w-4 mr-2" />
            <span className="hidden sm:inline">Sitting in the Power</span>
            <span className="sm:hidden">Power</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="practices" className="flex-1 overflow-auto px-1 max-w-full">
          <div className="space-y-4 pb-6 max-w-full overflow-hidden">
        {/* Week Progress Tracker */}
        <Card className="bg-gradient-to-br from-primary/10 via-accent/10 to-soft-accent/10 border-primary/20">
          <CardHeader>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <Calendar className="h-5 w-5 text-primary" />
                <CardTitle className="font-light">This Week's Development</CardTitle>
              </div>
              <Badge className="font-light bg-gradient-to-r from-primary to-accent text-primary-foreground border-0">
                {weekStats.days}/7 days practiced
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <ScrollArea className="w-full max-h-32">
              <div className="grid grid-cols-7 gap-1 sm:gap-2 mb-4 min-w-[280px]">
                {weekDates.map((date, idx) => {
                  const dayPractices = getPracticesForDate(date);
                  const isToday = format(date, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');
                  
                  return (
                     <div 
                      key={idx}
                      className={`flex flex-col items-center p-1.5 sm:p-2 md:p-3 rounded-lg transition-all ${
                        dayPractices.length > 0
                          ? 'bg-gradient-to-br from-primary via-accent to-secondary shadow-lg shadow-primary/20' 
                          : 'bg-muted/30 border-2 border-dashed border-muted-foreground/20'
                      } ${isToday ? 'ring-2 ring-accent' : ''}`}
                    >
                      <span className={`text-xs font-light mb-1 ${
                        dayPractices.length > 0 ? 'text-primary-foreground' : 'text-muted-foreground'
                      }`}>
                        {format(date, 'EEE')}
                      </span>
                      <div className={`text-lg font-light ${
                        dayPractices.length > 0 ? 'text-primary-foreground' : 'text-muted-foreground'
                      }`}>
                        {format(date, 'd')}
                      </div>
                      {dayPractices.length > 0 && (
                        <div className="flex items-center gap-1 mt-1">
                          <CheckCircle2 className="h-3 w-3 text-primary-foreground" />
                          <span className="text-xs text-primary-foreground font-light">{dayPractices.length}</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </ScrollArea>

          {weekStats.total > 0 && (
            <div className="space-y-2 pt-4 border-t border-primary/10">
              <p className="text-sm font-light text-muted-foreground">Most practiced this week:</p>
              <div className="flex flex-wrap gap-2">
                {Object.entries(weekStats.exercises)
                  .sort(([, a], [, b]) => b - a)
                  .slice(0, 3)
                  .map(([exercise, count]) => (
                    <Badge key={exercise} variant="outline" className="font-light">
                      {exercise} × {count}
                    </Badge>
                  ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Select Practice */}
      {!selectedExercise && (
        <Card className="border-accent/20">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 font-light">
              <Eye className="h-5 w-5 text-accent animate-pulse" />
              Choose Today's Practice
            </CardTitle>
            <CardDescription className="font-light">
              Select a practice to view instructions and journal about your experience
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[400px] pr-4">
              <div className="space-y-3">
                {exercises.map((exercise) => (
                  <Card
                    key={exercise.id}
                    className={`cursor-pointer transition-all hover:shadow-md ${
                      exercise.recommended 
                        ? 'border-primary/50 bg-gradient-to-r from-primary/10 to-accent/10 hover:from-primary/20 hover:to-accent/20' 
                        : 'hover:bg-accent/10 hover:border-accent/30'
                    }`}
                    onClick={() => handleExerciseSelect(exercise.id)}
                  >
                    <CardContent className="p-4">
                      <div className="space-y-2">
                        <div className="flex items-start justify-between gap-2">
                          <h4 className="font-light text-base">{exercise.name}</h4>
                          {exercise.recommended && (
                            <Badge className="text-xs font-light bg-gradient-to-r from-primary to-accent text-primary-foreground border-0 shrink-0">
                              ⭐ Foundation
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground font-light line-clamp-2">
                          {exercise.benefits}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
                
                <Card
                  className="cursor-pointer border-dashed border-2 border-secondary/30 hover:bg-secondary/10 hover:border-secondary/50 transition-all"
                  onClick={handleCustomExercise}
                >
                  <CardContent className="p-4">
                    <div className="space-y-1">
                      <h4 className="font-light text-base text-primary">+ Custom Practice</h4>
                      <p className="text-xs text-muted-foreground font-light">
                        Add your own third eye development practice
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      )}

      {/* Practice Session */}
      {selectedExercise && (
        <Card className="border-accent/20 bg-gradient-to-br from-primary/5 via-accent/5 to-soft-accent/5">
          <CardHeader>
            <div className="flex items-center justify-between flex-wrap gap-4">
              <CardTitle className="flex items-center gap-2 font-light">
                <Sparkles className="h-5 w-5 text-accent animate-pulse" />
                {selectedExercise === 'custom' ? customPracticeName : selectedExerciseData?.name}
              </CardTitle>
              {selectedExercise === 'sitting-in-power' && (
                <Button
                  size="sm"
                  className="gap-2 bg-gradient-to-r from-primary to-accent hover:from-primary-light hover:to-accent text-primary-foreground border-0"
                  onClick={() => navigate('/meditation-library')}
                >
                  <Zap className="h-4 w-4" />
                  Enhance the Power
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="max-w-full overflow-hidden">
            <ScrollArea className="h-[350px] sm:h-[450px] md:h-[500px] pr-2 sm:pr-4">
              <div className="space-y-4 sm:space-y-6 max-w-full overflow-hidden">
                {selectedExercise !== 'custom' && selectedExerciseData && (
                  <>
                    <div className="p-4 bg-gradient-to-br from-primary/10 to-accent/10 rounded-lg border border-primary/20 space-y-3 max-w-full overflow-hidden">
                      <div className="max-w-full overflow-hidden">
                        <h4 className="font-light text-primary mb-2">📖 Instructions</h4>
                        <p className="text-sm font-light leading-relaxed break-words overflow-wrap-anywhere">{selectedExerciseData.instructions}</p>
                      </div>
                      <div className="max-w-full overflow-hidden">
                        <h4 className="font-light text-accent mb-2">✨ Why This Helps</h4>
                        <p className="text-sm font-light leading-relaxed break-words overflow-wrap-anywhere">{selectedExerciseData.benefits}</p>
                      </div>
                    </div>
                  </>
                )}

                <div className="space-y-4">
                  <h4 className="font-light text-foreground">Journal Your Experience</h4>
                  {journalResponses.map((item, index) => (
                    <div key={index} className="space-y-2">
                      <Label className="font-light text-primary">{item.prompt}</Label>
                      <Textarea
                        value={item.response}
                        onChange={(e) => handleResponseChange(index, e.target.value)}
                        placeholder="Write your reflections here..."
                        rows={3}
                        className="font-light border-accent/20 focus:border-accent/50"
                      />
                    </div>
                  ))}
                </div>

                <div className="flex gap-2 pt-4">
                  <Button 
                    onClick={handleSavePractice} 
                    className="flex-1 bg-gradient-to-r from-primary to-accent hover:from-primary-light hover:to-accent text-primary-foreground border-0"
                  >
                    Save Practice
                  </Button>
                  <Button 
                    variant="outline" 
                    className="border-accent/30 hover:bg-accent/10"
                    onClick={() => {
                      setSelectedExercise(null);
                      setJournalResponses([]);
                      setCustomPracticeName('');
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      )}

      {/* Custom Practice Dialog */}
      <Dialog open={showCustomDialog} onOpenChange={setShowCustomDialog}>
        <DialogContent className="max-w-[95vw] sm:max-w-lg overflow-hidden">
          <DialogHeader>
            <DialogTitle className="font-light break-words">Create Custom Practice</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label className="font-light">Practice Name</Label>
              <Input
                value={customPracticeName}
                onChange={(e) => setCustomPracticeName(e.target.value)}
                placeholder="e.g., Crystal Gazing, Scrying, Mirror Work..."
                className="font-light"
              />
            </div>
            <Button onClick={startCustomPractice} className="w-full">
              Start Practice
            </Button>
          </div>
        </DialogContent>
      </Dialog>
          </div>
        </TabsContent>

        {/* Sitting in the Power Tab */}
        <TabsContent value="power" className="flex-1 overflow-auto px-1 max-w-full">
          <div className="space-y-4 pb-6 max-w-full overflow-hidden">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="max-w-full overflow-hidden">
                <CardTitle className="flex items-center gap-2 font-light">
                  <Lightbulb className="h-5 w-5 text-primary animate-pulse flex-shrink-0" />
                  <span className="break-words overflow-wrap-anywhere">Sitting in the Power</span>
                </CardTitle>
                <CardDescription className="font-light mt-2 break-words overflow-wrap-anywhere">
                  The foundational practice for all mediumship and psychic development. As taught in "The Psychic Psychologist®," this is where you build your spiritual power, raise your vibration, and strengthen your connection to the spirit realm. Through regular practice, you develop the energetic stamina, sensitivity, and presence needed for authentic psychic and mediumship work. It's not just sitting quietly - it's actively engaging with and expanding your spiritual power.
                </CardDescription>
              </div>
              <Button
                size="sm"
                className="gap-2 bg-gradient-to-r from-primary to-accent hover:from-primary-light hover:to-accent text-primary-foreground border-0 flex-shrink-0"
                onClick={() => navigate('/meditation-library?meditationId=aaa4dcc0-e26b-402e-97f2-1f5a6567fddf&returnPath=/telempathy-tools?tab=power')}
              >
                <Zap className="h-4 w-4" />
                <span className="hidden sm:inline">Enhance the Power</span>
                <span className="sm:hidden">Enhance</span>
              </Button>
            </div>
          </CardHeader>
          <CardContent className="max-w-full overflow-hidden">
            <ScrollArea className="h-[350px] sm:h-[450px] md:h-[550px] pr-2 sm:pr-4">
              <div className="space-y-4 sm:space-y-6 max-w-full overflow-hidden">
                {/* Practice Guide */}
                <div className="p-4 bg-muted/50 rounded-lg space-y-3 max-w-full overflow-hidden">
                  <h3 className="font-light text-sm break-words">How to Practice:</h3>
                  <ol className="text-sm space-y-2 list-decimal list-inside font-light break-words overflow-wrap-anywhere">
                    <li>Find a quiet space and sit comfortably with feet flat on the floor</li>
                    <li>Close your eyes and focus on your breathing</li>
                    <li>Visualise roots growing from your feet into the earth (grounding)</li>
                    <li>Draw energy up through your body from the earth</li>
                    <li>Open your crown chakra and invite spiritual energy to flow down</li>
                    <li>Allow your energy and spirit energy to blend and expand</li>
                    <li>Stay in this elevated state, observing sensations, images, or feelings</li>
                    <li>When ready, gently ground yourself and return to normal awareness</li>
                  </ol>
                  <p className="text-xs text-muted-foreground font-light mt-2 break-words overflow-wrap-anywhere">
                    Aim for 20-30 minutes per session. Regular daily practice yields the best results.
                  </p>
                </div>

                {/* Timer Section */}
                <div className="flex flex-col items-center gap-4 p-6 bg-card border border-border rounded-lg">
                  <div className="text-4xl font-mono font-bold text-primary">
                    {formatTime(timer)}
                  </div>
                  {!isSessionActive ? (
                    <Button onClick={startSession} size="lg" className="gap-2 bg-gradient-to-r from-primary to-accent hover:from-primary-light hover:to-accent text-primary-foreground">
                      <Play className="h-4 w-4" />
                      Start Session
                    </Button>
                  ) : (
                    <Button onClick={endSession} variant="secondary" size="lg" className="gap-2">
                      <Square className="h-4 w-4" />
                      End Session
                    </Button>
                  )}
                </div>

                {/* Session Notes Form */}
                {!isSessionActive && timer > 0 && (
                  <form onSubmit={(e) => { e.preventDefault(); savePowerSession(); }} className="space-y-4 p-4 border border-border rounded-lg bg-card animate-fade-in">
                    <h3 className="font-light">Session Notes</h3>
                    
                    <div className="space-y-2">
                      <Label className="font-light">Energy Quality</Label>
                      <Textarea
                        value={powerFormData.energy_quality}
                        onChange={(e) => setPowerFormData({ ...powerFormData, energy_quality: e.target.value })}
                        placeholder="How did the energy feel? (warm, tingling, expansive, peaceful, etc.)"
                        rows={2}
                        className="font-light"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label className="font-light">Physical Sensations</Label>
                      <Textarea
                        value={powerFormData.sensations_noted}
                        onChange={(e) => setPowerFormData({ ...powerFormData, sensations_noted: e.target.value })}
                        placeholder="Note any physical sensations (pressure, tingling, temperature changes, etc.)"
                        rows={2}
                        className="font-light"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label className="font-light">Insights & Impressions</Label>
                      <Textarea
                        value={powerFormData.insights_received}
                        onChange={(e) => setPowerFormData({ ...powerFormData, insights_received: e.target.value })}
                        placeholder="Any images, messages, feelings, or knowing that came through?"
                        rows={3}
                        className="font-light"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label className="font-light">Power Level: {powerFormData.power_rating}/10</Label>
                      <Slider
                        value={[powerFormData.power_rating]}
                        onValueChange={([value]) => setPowerFormData({ ...powerFormData, power_rating: value })}
                        min={0}
                        max={10}
                        step={1}
                      />
                      <p className="text-xs text-muted-foreground font-light">
                        How strong was your connection to your power/spirit energy?
                      </p>
                    </div>

                    <Button type="submit" className="w-full bg-gradient-to-r from-primary to-accent hover:from-primary-light hover:to-accent text-primary-foreground">
                      Save Session
                    </Button>
                  </form>
                )}

                {/* Session History */}
                <div className="space-y-3">
                  <h3 className="font-light">Recent Sessions</h3>
                  {powerSessions.length === 0 ? (
                    <p className="text-muted-foreground text-sm text-center py-4 font-light">
                      No sessions recorded yet. Start practicing to build your power!
                    </p>
                  ) : (
                    powerSessions.map((session) => (
                      <Card key={session.id} className="bg-muted/30">
                        <CardContent className="pt-4 space-y-2">
                          <div className="flex items-center justify-between flex-wrap gap-2">
                            <span className="text-sm font-light">
                              {format(new Date(session.session_date), 'PPP')}
                            </span>
                            <div className="flex gap-2">
                              <Badge variant="secondary" className="font-light">{session.duration_minutes} min</Badge>
                              <Badge variant="outline" className="font-light">Power: {session.power_rating}/10</Badge>
                            </div>
                          </div>
                          {session.energy_quality && (
                            <p className="text-sm text-muted-foreground font-light">
                              <span className="font-light">Energy:</span> {session.energy_quality}
                            </p>
                          )}
                          {session.insights_received && (
                            <p className="text-sm text-muted-foreground font-light">
                              <span className="font-light">Insights:</span> {session.insights_received}
                            </p>
                          )}
                        </CardContent>
                      </Card>
                    ))
                  )}
                </div>
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
